import { HTMLInputTypeAttribute, useState, useMemo, useEffect } from "react"
import useSlot from 'hooks/useSlot'
import useConfiguration from "hooks/useConfiguration"
import DatePicker from "react-multi-date-picker"
import type { Value } from "react-multi-date-picker"
import { Alphabet, Alphanumeric, NUMBER, NUMBERWITHDOT } from "helpers/regExp"
import Icon from "react-multi-date-picker/components/icon"
interface TextFieldProps {
    // ========================== INPUT ATTRIBUTE ====================================== //
    label?: string
    placeholder?: string
    type: HTMLInputTypeAttribute
    name?: string
    onInput?: Function
    onChange?: Function
    defaultValue?: string | number | readonly string[] | undefined | Value
    disabled?: boolean
    message?: string
    isAlphanumeric?: boolean
    isOnlyNumber?: boolean
    isOnlyAlphabet?: boolean
    isNumberWithDot?: boolean
    onFocus?: any
    // ======================== VALIDATION ============================ //
    pattern?: string
    title?: string
    required?: boolean
    maxLength?: number
    min?: any
    max?: any
    isToUpperCase?: boolean
    decimalDigits?: number
    autocomplateOff?: boolean
    justHour?: boolean
    // ======================== ADDITIONAL CLASS ====================================== //
    inputClass?: String

    // ========================= SLOT ============================= //
    children?: JSX.Element[] | JSX.Element

    // ========================== THEME CONFIGURATION =================================== //
    theme?: '1' | '2' | '3'
    themeCustomization?: 'true' | 'false'
}

const CustomTextField = (props: TextFieldProps) => {
    const { label, type, inputClass, name, children, onInput, defaultValue, disabled, theme, themeCustomization, placeholder, pattern, title, required, message, maxLength, onChange, min, max, isToUpperCase, isAlphanumeric, isOnlyNumber, decimalDigits, autocomplateOff, isOnlyAlphabet, onFocus, justHour, isNumberWithDot } = props
    const { config: { fieldType } } = useConfiguration()
    const [defaultTheme, setDefaultTheme] = useState('1')
    const [value, setValue] = useState(defaultValue ?? '')
    const [error, setError] = useState(false)
    const Slot = useSlot(children)

    useEffect(() => {
        if (defaultValue) setValue(defaultValue)
        if (defaultValue === '') setValue('')
    }, [defaultValue])

    const handleInput = (e: any) => {
        const newValue = e?.target?.value;
        if (type === 'number' && maxLength) {
            if (newValue.toString()?.length > maxLength) return
        }
        if (type === 'percentage' && maxLength) {
            if (Number(newValue?.toString()) > 100) return
            if (Number(newValue?.split('.')[1]) > Number('9'.repeat(decimalDigits ?? 1))) return

        }
        if (type !== 'yearPicker') {
            if (newValue?.length === 0 && required) {
                e.target.setCustomValidity('Silahkan isi field ini!')
            } else {
                e.target.setCustomValidity('')
            }
        }
        if (isOnlyNumber && newValue?.length > 0) {
            if (NUMBER.test(newValue)) {
                setValue(newValue)
                if (onInput) onInput(e)
            }
        } else if (isNumberWithDot && newValue?.length > 0) {
            if (NUMBERWITHDOT.test(newValue)) {
                setValue(newValue)
                if (onInput) onInput(e)
            }
        } else if (isAlphanumeric && newValue?.length > 0) {
            if (Alphanumeric.test(newValue)) {
                setValue(newValue)
                if (onInput) onInput(e)
            }
        } else if (isOnlyAlphabet && newValue?.length > 0) {
            if (Alphabet.test(newValue)) {
                setValue(newValue)
                if (onInput) onInput(e)
            }
        } else if (justHour && newValue?.length > 0) {
            const [hours] = newValue.split(':');
            setValue(`${hours}:00`)
            console.log("🚀 ~ handleInput ~ newValue:", newValue, hours)
            if (onInput) onInput(e)
        } else {
            setValue(newValue)
            if (onInput) onInput(e)
        }

        if (isToUpperCase) {
            setValue(newValue.toUpperCase())
            if (onInput) onInput(e)
        }

        if (onChange) {
            onChange(e)
        }
    }

    useMemo(() => {
        if (themeCustomization === 'false') setDefaultTheme(theme ?? '1')
        else setDefaultTheme(fieldType)
    }, [fieldType]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleBlur = (e: any) => {
        console.log(e.target.validationMessage)
        if (e.target.validationMessage) setError(true)
        else setError(false)
        console.log(error)
    }

    const preventMinus = (e: any) => {
        if (type === 'number') {
            if (e.code === 'Minus') {
                e.preventDefault();
            }
        }
    };

    return (
        <>
            <div className={"input-base " + inputClass} data-theme={theme ?? defaultTheme} theme-customization={themeCustomization ?? 'true'}>
                {
                    defaultTheme === '1' ?
                        <Slot name="preffix"></Slot> : null
                }
                <div className="field">
                    {
                        label ?
                            <label className={Slot({ name: 'suffix' }) && defaultTheme === '1' ? '!px-3' : ''}>{label} {required ? <b className="text-error-main">*</b> : ''}</label> : null
                    }
                    <div className={"relative w-full h-full flex " + ((Slot({ name: 'suffix' }) || Slot({ name: 'preffix' })) && (defaultTheme === '3' || defaultTheme === '2') ? 'input-base' : '')}>
                        {
                            defaultTheme === '2' || defaultTheme === '3' ?
                                <Slot name="preffix"></Slot> : null
                        }
                        {
                            type === 'yearPicker' ?
                                <div className="w-full pl-4 pr-4 flex items-center justify-between pb-1">
                                    <span className="">{new Date(value as Date).getFullYear()}</span>
                                    <DatePicker render={<Icon />} id={name} onlyYearPicker title={title} required={required} type={type} name={name} className={(Slot({ name: 'suffix' }) || Slot({ name: 'preffix' })) && (defaultTheme === '3' || defaultTheme === '2') ? '!border-none' : Slot({ name: 'preffix' }) ? '!px-3' : ''} value={value as Date} disabled={disabled} onChange={handleInput} placeholder={placeholder ?? 'Masukkan ' + label} inputClass="border-0 !h-10" portal />
                                </div> :
                                <input
                                    onFocus={onFocus}
                                    min={min}
                                    max={max}
                                    onKeyDown={preventMinus}
                                    maxLength={type === 'percentage' ? ((maxLength ?? 64) + (decimalDigits ?? 1)) : (maxLength ?? 64)}
                                    autoComplete={autocomplateOff ? "off" : "false"}
                                    onBlur={handleBlur} title={title}
                                    pattern={pattern} required={required}
                                    type={type === 'percentage' ? 'number' : type}
                                    name={name}
                                    className={(Slot({ name: 'suffix' }) || Slot({ name: 'preffix' })) && (defaultTheme === '3' || defaultTheme === '2') ? '!border-none' : Slot({ name: 'preffix' }) ? '!px-3' : ''}
                                    value={value !== null && value !== undefined ? value.toString() : ''}
                                    disabled={disabled} onInput={handleInput} placeholder={placeholder ?? 'Input ' + label} />
                        }
                        {
                            defaultTheme === '2' || defaultTheme === '3' ?
                                <Slot name="suffix"></Slot> : null
                        }
                    </div>
                </div>
                {
                    defaultTheme === '1' ?
                        <Slot name="suffix"></Slot> : null
                }
            </div>
            {message ?
                <p className="text-xs ml-2 text-red-500 p-0 m-0">{message}</p> :
                null
            }
        </>
    )
}

export default CustomTextField